const activeMonitors = new Map();

export const isFutureDate = (value, now = new Date()) => {
  if (!value) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return false;
  const todayUtc = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  );
  return parsed.getTime() > todayUtc;
};

export const buildCompanyRestrictionMessage = (permissions) => {
  if (!permissions) {
    return "Your company access is restricted. Please contact support.";
  }

  if (permissions.status === "blocked") {
    return "Your company has been blocked due to a policy violation. Please contact support.";
  }

  if (permissions.status === "suspended") {
    return "Your company is currently suspended. Please contact support to restore access.";
  }

  if (permissions.status === "paused") {
    return "Your company access is paused. Please contact support to reactivate access.";
  }

  if (!permissions.subscription_active) {
    return "Your company subscription has expired. Please renew your subscription to continue.";
  }

  return "Your company access is currently restricted. Please contact support.";
};

const normalizeStatus = (value) => String(value || "active").toLowerCase();

const parseDateOnly = (value) => {
  if (typeof value !== "string") return "";
  if (value.includes("T")) return value.split("T")[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
};

const daysUntil = (dateOnly, now = new Date()) => {
  if (!dateOnly) return 0;
  const target = new Date(`${dateOnly}T00:00:00Z`);
  if (Number.isNaN(target.getTime())) return 0;
  const todayUtc = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  );
  return Math.max(0, Math.ceil((target.getTime() - todayUtc) / 86400000));
};

export const deriveCompanyAccessState = (companyData, now = new Date()) => {
  const status = normalizeStatus(
    companyData?.subscriptionStatus || companyData?.status
  );
  const statusReason =
    companyData?.subscriptionStatusReason || companyData?.status_reason || "";
  const expiresAt = parseDateOnly(
    companyData?.subscriptionExpiresAt || companyData?.subscription_expires_at
  );
  const subscriptionActive = status === "active" && isFutureDate(expiresAt, now);
  const canOperate =
    status !== "blocked" && status !== "suspended" && status !== "paused" && subscriptionActive;

  return {
    status,
    status_reason: statusReason,
    subscription_active: subscriptionActive,
    is_subscription_active: subscriptionActive,
    can_operate: canOperate,
    can_read_data: canOperate,
    can_write_data: canOperate,
    subscription_expires_at: expiresAt || null,
    days_until_expiry: daysUntil(expiresAt, now),
    data_limit_mb: 0,
    data_used_mb: 0,
    data_usage_percentage: 0,
  };
};

export const startCompanyAccessMonitor = ({
  monitorKey,
  checkAccess,
  onRestricted,
  onRestored,
  intervalMs = 60000,
}) => {
  if (!monitorKey || typeof checkAccess !== "function") return () => {};
  const existing = activeMonitors.get(monitorKey);
  if (existing) clearInterval(existing);

  let wasRestricted = false;
  const timer = setInterval(async () => {
    const result = await checkAccess();
    if (result?.restricted && typeof onRestricted === "function") {
      wasRestricted = true;
      onRestricted(result.reason);
    } else if (!result?.restricted && wasRestricted && typeof onRestored === "function") {
      wasRestricted = false;
      onRestored();
    }
  }, intervalMs);

  activeMonitors.set(monitorKey, timer);
  return () => {
    clearInterval(timer);
    if (activeMonitors.get(monitorKey) === timer) {
      activeMonitors.delete(monitorKey);
    }
  };
};
